// 100 frequently-asked questions across SA tax, IFRS, Companies Act, payroll,
// VAT, estate duty and trusts. Answers are deliberately advisory in tone —
// they explain the principle and direct the reader to book a consultation
// with TC Auditors for tailored advice. Education without enabling DIY.

export const faqCategories = [
    "All",
    "Income Tax",
    "VAT",
    "Provisional Tax",
    "PAYE & UIF",
    "Companies Act",
    "Trusts",
    "Estate Duty & Wills",
    "Financial Statements",
    "IFRS",
    "Xero & Software",
    "SARS Disputes",
];

const consult = "Speak to TC Auditors before acting — every business has unique facts that change the answer.";

export const faqs = [
    // ============================================================
    // INCOME TAX (12)
    // ============================================================
    {
        category: "Income Tax",
        question: "What is the current corporate income tax rate in South Africa?",
        answer:
            "Companies are taxed at 27% of taxable income for years of assessment ending on or after 31 March 2023. Small Business Corporations (SBCs) qualify for a sliding-scale rate that starts at 0%. " + consult,
    },
    {
        category: "Income Tax",
        question: "What is a Small Business Corporation (SBC) and how do I qualify?",
        answer:
            "SBC status under s.12E of the Income Tax Act offers reduced corporate tax rates and accelerated wear & tear, but the company must meet strict criteria around shareholder type, gross income limits and personal-service exclusions. We assess SBC eligibility annually as part of every Core and Elite engagement.",
    },
    {
        category: "Income Tax",
        question: "When is my company's annual ITR14 income tax return due?",
        answer:
            "An ITR14 must be filed within 12 months of the company's year-end. Late filing exposes the company to administrative penalties and understatement penalties on any tax shortfall. " + consult,
    },
    {
        category: "Income Tax",
        question: "Do I have to pay tax on a dividend I declare to myself?",
        answer:
            "Dividends declared by SA resident companies attract Dividends Tax of 20% (withheld and paid over by the company). Certain shareholders (e.g. SA resident companies) are exempt with the correct DTD(EX) declaration on file.",
    },
    {
        category: "Income Tax",
        question: "What expenses can I deduct from my business income?",
        answer:
            "Section 11(a) allows a deduction of expenses actually incurred in the production of income, not capital in nature. Capital items qualify for wear-and-tear under s.11(e) or s.12C/E. The line between capital and revenue is fact-specific — let us review your largest items annually.",
    },
    {
        category: "Income Tax",
        question: "Are home office expenses deductible?",
        answer:
            "Only if a portion of your home is exclusively used for trade and is specifically equipped for that purpose. SARS scrutinises these claims closely. " + consult,
    },
    {
        category: "Income Tax",
        question: "How is interest from my bank account taxed?",
        answer:
            "Individuals enjoy an annual interest exemption (R23,800 if under 65, R34,500 if 65+). Companies have no exemption — all interest is fully taxable. Dividends from listed shares are exempt but Dividends Tax already applied.",
    },
    {
        category: "Income Tax",
        question: "What is Capital Gains Tax (CGT) and when does it apply?",
        answer:
            "CGT applies when a 'capital asset' is disposed of. The taxable inclusion rate is 80% for companies and trusts, and 40% for individuals. Primary residence and small-business relief may reduce the gain. Plan disposals well in advance with us.",
    },
    {
        category: "Income Tax",
        question: "Can I claim a tax loss from one year against future profits?",
        answer:
            "Yes. Assessed losses are ring-fenced and may be set off against future income, subject to the s.20 80% cap that applies to companies whose taxable income exceeds R1m. " + consult,
    },
    {
        category: "Income Tax",
        question: "What is the difference between a tax-deductible expense and a non-deductible one?",
        answer:
            "Tax-deductible expenses reduce taxable income (e.g. salaries, rent, marketing). Non-deductible items include fines, penalties, donations to non-PBOs, and certain entertainment. We test every general ledger account against the Income Tax Act before submission.",
    },
    {
        category: "Income Tax",
        question: "Do I pay tax on foreign income?",
        answer:
            "SA residents are taxed on worldwide income. Foreign tax credits under s.6quat may apply. The s.10(1)(o)(ii) foreign-employment exemption is capped at R1.25m. Cross-border tax is highly fact-sensitive. " + consult,
    },
    {
        category: "Income Tax",
        question: "Are donations to a charity tax-deductible?",
        answer:
            "Only donations to organisations holding a valid s.18A PBO certificate qualify, and the deduction is capped at 10% of taxable income for individuals and companies. Excess can roll forward.",
    },

    // ============================================================
    // VAT (12)
    // ============================================================
    {
        category: "VAT",
        question: "When am I required to register for VAT?",
        answer:
            "VAT registration is compulsory once taxable supplies exceed R2.3 million in any consecutive 12-month period. Voluntary registration is allowed once turnover exceeds R120,000. These thresholds were increased from R1 million and R50,000 respectively in the 2026 Budget, effective 1 April 2026. We can model whether voluntary registration helps or hurts your cash flow.",
    },
    {
        category: "VAT",
        question: "What is the current standard VAT rate?",
        answer:
            "The standard rate is 15%. Zero-rated supplies (e.g. exports, certain foodstuffs) carry 0% VAT, while exempt supplies (e.g. financial services, residential rentals) carry no VAT and no input claim.",
    },
    {
        category: "VAT",
        question: "How often do I submit a VAT201 return?",
        answer:
            "Most vendors fall under Category B and submit bi-monthly. Larger vendors (>R30m turnover) submit Category C monthly. Returns are due by the 25th of the following month, or the last business day if filed via SARS eFiling.",
    },
    {
        category: "VAT",
        question: "Can I claim VAT on a vehicle?",
        answer:
            "Generally no. Input VAT is denied on motor cars (sedans, double-cabs, SUVs) under s.17(2)(c), even when used for business. Light delivery vehicles and bakkies without rear seats may qualify.",
    },
    {
        category: "VAT",
        question: "What is a valid tax invoice?",
        answer:
            "A full tax invoice must contain the words 'Tax Invoice', supplier name, address, VAT number, invoice number, date, customer details (above R5,000), description of goods, value and VAT charged. SARS regularly disallows input VAT on invalid invoices.",
    },
    {
        category: "VAT",
        question: "How does VAT work on imported goods?",
        answer:
            "VAT on imports is paid to SARS Customs at clearance. The bill of entry serves as your input VAT documentation. Deferred VAT facilities are available to qualifying importers.",
    },
    {
        category: "VAT",
        question: "Do I charge VAT on services to a foreign client?",
        answer:
            "Services to a non-resident not in SA at the time of supply are generally zero-rated under s.11(2)(l), provided detailed contractual evidence is retained. Get this reviewed before invoicing — it is one of SARS's audit hotspots.",
    },
    {
        category: "VAT",
        question: "What happens if I don't submit my VAT return on time?",
        answer:
            "SARS imposes a 10% late-payment penalty plus interest at the prescribed rate, and may impose understatement penalties. Persistent non-compliance can lead to the deregistration of your VAT number.",
    },
    {
        category: "VAT",
        question: "Can I deregister for VAT if my turnover drops?",
        answer:
            "Yes — once you reasonably expect taxable supplies to fall below R2.3m (the compulsory threshold, effective 1 April 2026), deregistration is permitted. Existing vendors below the new threshold must apply — it is not automatic. SARS will levy output VAT on assets retained at deregistration date, so plan timing carefully.",
    },
    {
        category: "VAT",
        question: "How is VAT applied to deposits and prepayments?",
        answer:
            "VAT is triggered at the earlier of invoice or any payment received — including deposits. Many SMEs forget to declare deposits in the period received. We catch this in monthly Xero reviews.",
    },
    {
        category: "VAT",
        question: "What is the VAT treatment of interest charged on overdue accounts?",
        answer:
            "Interest, by definition a financial service, is exempt from VAT. Late-payment fees, however, may attract VAT. The wording on your invoice matters.",
    },
    {
        category: "VAT",
        question: "Are entertainment costs claimable for input VAT?",
        answer:
            "Generally no. Section 17(2)(a) denies input VAT on entertainment unless your business is in the trade of supplying entertainment (e.g. restaurant). " + consult,
    },

    // ============================================================
    // PROVISIONAL TAX (6)
    // ============================================================
    {
        category: "Provisional Tax",
        question: "Who is required to register as a provisional taxpayer?",
        answer:
            "All companies are automatically provisional taxpayers. Individuals earning income other than remuneration above R30,000 (or owning a rental property, or earning interest above the exemption) must register as provisional taxpayers.",
    },
    {
        category: "Provisional Tax",
        question: "When are provisional tax payments due?",
        answer:
            "First (IRP6/01) is due 6 months into the year of assessment, the second (IRP6/02) by year-end, and the optional 'top-up' third payment by 7 months after year-end (or 6 months for non-Feb year-ends).",
    },
    {
        category: "Provisional Tax",
        question: "What happens if my second provisional estimate is too low?",
        answer:
            "If taxable income exceeds R1m and the second IRP6 estimate is less than 80% of actual, SARS imposes a 20% under-estimation penalty under para 20 of the Fourth Schedule. We model this every year-end.",
    },
    {
        category: "Provisional Tax",
        question: "Can I use a 'basic amount' for provisional tax?",
        answer:
            "Yes — for the first provisional payment SARS allows the prior assessed taxable income (the 'basic amount') if no estimate is filed. Caveats apply for high-income taxpayers. " + consult,
    },
    {
        category: "Provisional Tax",
        question: "Do I have to file a provisional return if I have a tax loss?",
        answer:
            "Yes. A nil IRP6 must still be submitted. Failure to submit triggers SARS administrative penalties even when no tax is due.",
    },
    {
        category: "Provisional Tax",
        question: "How does the third top-up provisional payment work?",
        answer:
            "The third 'top-up' is voluntary and stops interest running on under-paid tax. It is due by 7 months after year-end (companies with February year-end) or 6 months otherwise.",
    },

    // ============================================================
    // PAYE & UIF (10)
    // ============================================================
    {
        category: "PAYE & UIF",
        question: "When must I register as an employer with SARS?",
        answer:
            "Within 21 business days of becoming an employer that is liable to deduct PAYE — typically when you employ anyone earning above the tax threshold.",
    },
    {
        category: "PAYE & UIF",
        question: "What is UIF and how is it calculated?",
        answer:
            "Unemployment Insurance Fund contributions are 2% of remuneration (1% employee, 1% employer), capped at a remuneration ceiling of R17,712 per month (currently). Contributions are paid via EMP201.",
    },
    {
        category: "PAYE & UIF",
        question: "What is the Skills Development Levy (SDL)?",
        answer:
            "SDL is 1% of payroll, paid by employers whose annual payroll exceeds R500,000. Funds are recoverable through SETA-accredited training programmes.",
    },
    {
        category: "PAYE & UIF",
        question: "Are independent contractors subject to PAYE?",
        answer:
            "Genuine independent contractors are not subject to PAYE — but the SARS 'dominant impression' test is strict. Mis-classification exposes the company to backdated PAYE, UIF, SDL, penalties and interest. We review your contractor base annually.",
    },
    {
        category: "PAYE & UIF",
        question: "What is the EMP501 reconciliation?",
        answer:
            "Employers reconcile PAYE/UIF/SDL paid (EMP201s) against IRP5 / IT3(a) certificates twice a year — interim by end October, annual by end May. Mis-reconciliations are a leading cause of SARS letters.",
    },
    {
        category: "PAYE & UIF",
        question: "How is a directors' remuneration package optimised?",
        answer:
            "Working director vs non-executive director, salary vs dividend mix, retirement annuity contributions, fringe benefits and travel allowances are all levers. " + consult,
    },
    {
        category: "PAYE & UIF",
        question: "Do I have to register for COIDA / Workmen's Compensation?",
        answer:
            "Yes — every employer with one or more employees must register with the Compensation Fund. Annual ROE returns and assessments follow.",
    },
    {
        category: "PAYE & UIF",
        question: "Can I pay an employee a fixed travel allowance?",
        answer:
            "Yes. 80% of the allowance is subject to PAYE (20% if the employer is satisfied 80%+ of usage is business). The employee then claims actual or fixed-cost rates on assessment.",
    },
    {
        category: "PAYE & UIF",
        question: "What is a fringe benefit?",
        answer:
            "Any benefit in kind given to employees (cell phone, vehicle, accommodation, low-interest loans). Section 7 of the Seventh Schedule values each benefit. Most are taxable to the employee.",
    },
    {
        category: "PAYE & UIF",
        question: "Are bursaries to employees' children tax-free?",
        answer:
            "Yes, within annual limits and subject to s.10(1)(q) and (qA) requirements. There are caps based on the employee's salary and the qualification level. We can structure this for you.",
    },

    // ============================================================
    // COMPANIES ACT (12)
    // ============================================================
    {
        category: "Companies Act",
        question: "When does my company need an audit vs an independent review?",
        answer:
            "Determined by your Public Interest Score (PIS) per Companies Regulation 26 + 28. Audits are required where PIS is 350+ (or 100–349 with internally-compiled AFS), or where mandated in your MOI. Reviews suffice for many SMEs. We compute PIS as part of every year-end.",
    },
    {
        category: "Companies Act",
        question: "What is the Public Interest Score (PIS)?",
        answer:
            "A score made up of average employees, third-party debt, turnover and shareholders. It drives whether your AFS need to be audited or independently reviewed and which IFRS framework you must apply.",
    },
    {
        category: "Companies Act",
        question: "When is the CIPC annual return due?",
        answer:
            "CIPC annual returns are due within 30 business days after the anniversary of incorporation. Failure to file for 2+ consecutive years results in deregistration of the company.",
    },
    {
        category: "Companies Act",
        question: "What is the Beneficial Ownership Register?",
        answer:
            "All companies must lodge a Beneficial Ownership disclosure with CIPC at registration, on annual return, and within 10 business days of any change. Non-compliance carries administrative fines.",
    },
    {
        category: "Companies Act",
        question: "Can I have a single director and shareholder?",
        answer:
            "Yes — a private company under the Companies Act 71 of 2008 may have one director and one shareholder (the same person). Larger or audited companies require additional directors.",
    },
    {
        category: "Companies Act",
        question: "What is the difference between a Pty (Ltd) and a CC?",
        answer:
            "No new Close Corporations may be incorporated since 2011. Existing CCs continue under the Companies Act. Pty (Ltd)s have shareholders and a separate board, while CCs have members only.",
    },
    {
        category: "Companies Act",
        question: "Do I need an MOI?",
        answer:
            "Every company has a Memorandum of Incorporation. The default CoR15.1A applies if you do not adopt a custom MOI. Custom MOIs are needed where shareholders agree on bespoke voting, transfer restrictions or pre-emption rights.",
    },
    {
        category: "Companies Act",
        question: "How do I change directors?",
        answer:
            "By board / shareholder resolution lodged on CIPC's CoR39 within 10 business days of the change. Late filings carry penalties. We handle this in our Secretarial service.",
    },
    {
        category: "Companies Act",
        question: "What is a solvency and liquidity test?",
        answer:
            "Section 4 requires the board to confirm a company is both solvent (assets > liabilities) and liquid (able to pay debts for 12 months) before declaring dividends, buying back shares or financial assistance.",
    },
    {
        category: "Companies Act",
        question: "Can a private company buy back its own shares?",
        answer:
            "Yes — subject to s.46 and s.48 (solvency and liquidity test, board resolution). Share buy-backs may also trigger Dividends Tax. " + consult,
    },
    {
        category: "Companies Act",
        question: "What is a BEE Affidavit?",
        answer:
            "Exempted Micro Enterprises (turnover < R10m) and Qualifying Small Enterprises (turnover R10m–R50m, 51%+ black-owned) may use a sworn affidavit instead of a verification certificate. We draft these for clients.",
    },
    {
        category: "Companies Act",
        question: "Do I need to keep minutes of meetings?",
        answer:
            "Yes — directors' resolutions, shareholder resolutions, share registers and minutes must be kept for 7 years per s.24. This is one of the first items SARS and CIPC ask for under examination.",
    },

    // ============================================================
    // TRUSTS (8)
    // ============================================================
    {
        category: "Trusts",
        question: "What types of trusts exist in South Africa?",
        answer:
            "Inter vivos (living) trusts and testamentary trusts (created by a will), which may be discretionary or vested, and bewind trusts (trustees administer for vested beneficiaries). Each has different tax and estate-planning implications.",
    },
    {
        category: "Trusts",
        question: "How is a trust taxed in South Africa?",
        answer:
            "Trusts are taxed at 45% — the highest marginal rate. The s.7 'conduit principle' may attribute income/gains to donors or beneficiaries at lower rates. Section 7C imposes deemed donations on interest-free loans to trusts.",
    },
    {
        category: "Trusts",
        question: "What is a 'sham trust' and why does it matter?",
        answer:
            "Where a trust is in name only and the founder still controls assets as their own, courts and SARS may pierce the veil and tax the assets in the founder's hands. Maintain genuine trustee independence and proper minutes.",
    },
    {
        category: "Trusts",
        question: "Do trusts pay provisional tax?",
        answer:
            "Yes. Trusts are provisional taxpayers and submit IRP6 returns twice yearly plus an annual IT12TR. We handle this end-to-end for trustee clients.",
    },
    {
        category: "Trusts",
        question: "What is a beneficiary's loan account in a trust?",
        answer:
            "Where income vests in a beneficiary but is not distributed, it sits as a loan account in their favour. Section 7C and estate-duty implications apply at death. " + consult,
    },
    {
        category: "Trusts",
        question: "Can I sell my house to a trust?",
        answer:
            "Yes, but the transaction triggers transfer duty/CGT, and the loan-back creates s.7C deemed-donation issues. Properly structured, the asset is removed from your estate. " + consult,
    },
    {
        category: "Trusts",
        question: "Who can be a trustee?",
        answer:
            "Any natural or juristic person of legal capacity. Best practice (and the Master's preference) is to include at least one independent professional trustee — a role TC Auditors regularly fulfils.",
    },
    {
        category: "Trusts",
        question: "What records must a trust keep?",
        answer:
            "Trust deed, Letters of Authority, minutes, asset registers, financial statements, beneficial-ownership register (per the GLAA Amendment) and tax returns must be kept and lodged with the Master annually.",
    },

    // ============================================================
    // ESTATE DUTY & WILLS (8)
    // ============================================================
    {
        category: "Estate Duty & Wills",
        question: "What is Estate Duty and what is the rate?",
        answer:
            "Estate Duty is levied on the dutiable value of a deceased estate at 20% on the first R30m and 25% above R30m. Section 4A grants a R3.5m abatement (portable between spouses).",
    },
    {
        category: "Estate Duty & Wills",
        question: "Why do I need a Will?",
        answer:
            "Without a valid will, the Intestate Succession Act decides who inherits — often with unintended consequences. A will also nominates executors, guardians for minor children and trusts for legacies.",
    },
    {
        category: "Estate Duty & Wills",
        question: "What is the spousal abatement (s.4q)?",
        answer:
            "Property bequeathed to a surviving spouse is deductible for estate duty under s.4(q). Combined with the R3.5m s.4A abatement that can roll over, married couples can shelter R7m before duty.",
    },
    {
        category: "Estate Duty & Wills",
        question: "How does CGT work on death?",
        answer:
            "Death is a deemed disposal at market value (s.9HA). Roll-over relief applies to assets left to a surviving spouse. Primary residence and small-business reliefs may further reduce gains.",
    },
    {
        category: "Estate Duty & Wills",
        question: "Do retirement annuities form part of my estate?",
        answer:
            "Approved retirement-fund benefits are generally excluded from estate duty (s.3(2)(i)) but may attract executor's fees if paid to the estate. Beneficiary nominations matter.",
    },
    {
        category: "Estate Duty & Wills",
        question: "How long does winding up an estate take?",
        answer:
            "On average 9–18 months. Master of the High Court reporting, advertising for creditors, SARS deceased-estate registration, asset realisation and the L&D account each take time. We work alongside your attorney to streamline this.",
    },
    {
        category: "Estate Duty & Wills",
        question: "What is donations tax?",
        answer:
            "20% on donations above R30m cumulative, 25% above; first R100,000 per individual per tax year is exempt. Donations between spouses are exempt. Used in lifetime estate planning.",
    },
    {
        category: "Estate Duty & Wills",
        question: "Can I disinherit my spouse or child?",
        answer:
            "Spouses are protected by the Maintenance of Surviving Spouses Act and the matrimonial property regime. Minor children may have a maintenance claim. Adult children can be disinherited — but the will must be carefully drafted. " + consult,
    },

    // ============================================================
    // FINANCIAL STATEMENTS (12)
    // ============================================================
    {
        category: "Financial Statements",
        question: "What is the difference between Audited, Reviewed and Compiled AFS?",
        answer:
            "Audit (ISA) — reasonable assurance, opinion. Independent Review (ISRE 2400) — limited assurance, conclusion. Compilation (ISRS 4410) — no assurance, just a presentation of management's figures. Each is determined by your Public Interest Score and MOI.",
    },
    {
        category: "Financial Statements",
        question: "When are my AFS due?",
        answer:
            "AFS must be finalised within 6 months of year-end per the Companies Act. They must be circulated to shareholders before the AGM and submitted to CIPC via XBRL with the annual return.",
    },
    {
        category: "Financial Statements",
        question: "What is XBRL and why does CIPC require it?",
        answer:
            "eXtensible Business Reporting Language is a standardised digital tagging format. Since 2018 CIPC requires AFS to be filed in iXBRL — we file these for our clients within the annual return.",
    },
    {
        category: "Financial Statements",
        question: "Who can sign off audited financial statements?",
        answer:
            "Only a Registered Auditor (RA) registered with IRBA may issue an audit opinion. TC Auditors holds an active IRBA registration.",
    },
    {
        category: "Financial Statements",
        question: "Do dormant companies still need AFS?",
        answer:
            "Yes — a dormant company must still file an annual return and (in most cases) AFS, even if both are nil. Dormant entities are deregistered by CIPC if returns are not filed.",
    },
    {
        category: "Financial Statements",
        question: "Can I prepare my own AFS as a director?",
        answer:
            "Where the AFS are 'internally compiled' the Public Interest Score threshold for audit drops from 350 to 100. It is almost always cheaper to have us compile.",
    },
    {
        category: "Financial Statements",
        question: "What is a going concern assertion?",
        answer:
            "Directors must assess the company's ability to continue trading for at least 12 months from sign-off date. Material uncertainties must be disclosed. Auditors / reviewers test this assertion every year.",
    },
    {
        category: "Financial Statements",
        question: "Who appoints the auditor?",
        answer:
            "Shareholders appoint the auditor at each AGM; for first year auditees, the directors appoint the first auditor within 40 business days of incorporation. Audit firms must rotate every 5 years for listed companies.",
    },
    {
        category: "Financial Statements",
        question: "Are management accounts the same as financial statements?",
        answer:
            "No — management accounts are internal, monthly / quarterly tools. AFS are formal, annual, IFRS-compliant statements signed by directors and (where required) an auditor or reviewer.",
    },
    {
        category: "Financial Statements",
        question: "What is included in a set of AFS?",
        answer:
            "Statement of Financial Position, Statement of Profit or Loss & OCI, Statement of Changes in Equity, Statement of Cash Flows, Notes, Directors' Report, and (where applicable) Auditor's / Reviewer's report.",
    },
    {
        category: "Financial Statements",
        question: "How long must I keep accounting records?",
        answer:
            "At least 7 years per s.24 of the Companies Act and s.29 of the Tax Administration Act. Cloud-based Xero records give automatic 7-year retention.",
    },
    {
        category: "Financial Statements",
        question: "Why does my bank ask for signed AFS?",
        answer:
            "Banks rely on signed, dated AFS — preferably reviewed or audited — to assess credit. Compiled AFS by a CA(SA) carry credibility. We work with all major SA banks.",
    },

    // ============================================================
    // IFRS (5)
    // ============================================================
    {
        category: "IFRS",
        question: "Should I apply full IFRS or IFRS for SMEs?",
        answer:
            "Driven by Public Interest Score and MOI. Most SME owner-managed businesses apply IFRS for SMEs which is simpler and shorter. Listed companies must apply full IFRS.",
    },
    {
        category: "IFRS",
        question: "What changed under IFRS 16 Leases?",
        answer:
            "Lessees recognise nearly all leases on the balance sheet as a right-of-use asset and lease liability. The old operating-lease off-balance-sheet treatment is gone.",
    },
    {
        category: "IFRS",
        question: "How do I recognise revenue under IFRS 15?",
        answer:
            "Five steps: identify contract → identify performance obligations → determine transaction price → allocate → recognise revenue when (or as) obligations are satisfied. Construction, software and subscription businesses are most affected.",
    },
    {
        category: "IFRS",
        question: "What is fair value and when do I use it?",
        answer:
            "IFRS 13 defines fair value as the price to sell an asset / transfer a liability in an orderly transaction. It applies to financial instruments, investment property, biological assets and certain PPE classes.",
    },
    {
        category: "IFRS",
        question: "How do I impair a debtor under IFRS 9?",
        answer:
            "Apply the simplified Expected Credit Loss model — usually a provision matrix based on historical loss rates adjusted for forward-looking factors. We help build this matrix for clients each year.",
    },

    // ============================================================
    // XERO & SOFTWARE (5)
    // ============================================================
    {
        category: "Xero & Software",
        question: "Why does TC Auditors recommend Xero?",
        answer:
            "Xero is the world-leading cloud accounting platform with bank feeds for every major SA bank, SARS-ready VAT201 export, an App Store of 1,000+ integrations, and beautiful reporting. As a Xero Gold Partner we license at preferential rates.",
    },
    {
        category: "Xero & Software",
        question: "What is the Xero App Store?",
        answer:
            "A marketplace of 1,000+ apps that plug into Xero — for inventory (Unleashed, DEAR), payroll (SimplePay), debtor management (Chaser), POS (Vend / Loyverse), e-commerce (Shopify, WooCommerce) and more.",
    },
    {
        category: "Xero & Software",
        question: "Can Xero handle multi-currency?",
        answer:
            "Yes. Xero's Premium plan supports multi-currency invoicing, bills, bank accounts and automatic FX gain/loss postings — perfect for South African importers and exporters.",
    },
    {
        category: "Xero & Software",
        question: "How do you migrate me from Pastel / Sage / Quickbooks to Xero?",
        answer:
            "We perform a structured cut-over: trial-balance import at a chosen conversion date, migration of master files (customers, suppliers, items), bank-feed activation and 3 years of comparative data. Average migration: 2–4 weeks.",
    },
    {
        category: "Xero & Software",
        question: "Is Xero secure?",
        answer:
            "Yes — Xero is ISO 27001 certified, applies AES-256 at rest, TLS in transit, and offers two-step authentication. Data is hosted in secure global data centres with daily backups.",
    },

    // ============================================================
    // SARS DISPUTES (8)
    // ============================================================
    {
        category: "SARS Disputes",
        question: "What is a SARS verification?",
        answer:
            "SARS verifications check supporting documents for a return — usually within 21 business days. Failure to upload all requested documents leads to additional assessments. We handle verifications for clients on retainer.",
    },
    {
        category: "SARS Disputes",
        question: "What is the difference between an objection and an appeal?",
        answer:
            "An objection (NOO) is your first formal challenge to an assessment, due within 80 business days. An appeal (NOA) follows if the objection is disallowed, escalated to the Tax Board or Tax Court. Strict timelines apply.",
    },
    {
        category: "SARS Disputes",
        question: "Can I get a tax debt written off?",
        answer:
            "Compromise of tax debt under Chapter 14 of the TAA is possible where SARS accepts that the taxpayer cannot reasonably pay. The taxpayer's full financial position must be disclosed. " + consult,
    },
    {
        category: "SARS Disputes",
        question: "What is the Voluntary Disclosure Programme (VDP)?",
        answer:
            "VDP allows taxpayers to disclose undeclared income, claim relief from up to 100% of administrative penalties and avoid criminal prosecution. Disclosure must be voluntary and complete.",
    },
    {
        category: "SARS Disputes",
        question: "How do interest and penalties work on late tax?",
        answer:
            "Interest at the prescribed rate (currently 11.5%) runs daily, plus 10% late-payment penalty on PAYE and VAT. Understatement penalties range 0%–200% based on behaviour (substantial understatement, reasonable care, intentional tax evasion).",
    },
    {
        category: "SARS Disputes",
        question: "Can SARS attach my business bank account?",
        answer:
            "Yes — under s.179 of the TAA, SARS can issue a third-party appointment to your bank for unpaid tax. We resolve these through urgent payment arrangements and compromises.",
    },
    {
        category: "SARS Disputes",
        question: "What is a SARS audit and how do I survive one?",
        answer:
            "An audit is a deep examination of returns over multiple years. The single best preparation is clean, IFRS-compliant Xero books, supporting documents organised digitally, and a registered tax practitioner representing you.",
    },
    {
        category: "SARS Disputes",
        question: "How do I get a Tax Compliance Status (TCS) certificate?",
        answer:
            "Apply via SARS eFiling → Tax Status. SARS issues a PIN that third parties can verify online. Required for tenders, emigration, foreign investment allowances and good-standing letters.",
    },
];

// Sanity: should equal 100
// 12 + 12 + 6 + 10 + 12 + 8 + 8 + 12 + 5 + 5 + 8 = 98 — let's pad with 2 extra to hit exactly 100
faqs.push(
    {
        category: "Income Tax",
        question: "Are crypto-asset gains taxed in South Africa?",
        answer:
            "SARS treats crypto as an asset of an intangible nature. Gains are either revenue (frequent traders) or capital (long-term investors). Disposal triggers either income tax or CGT depending on intention. " + consult,
    },
    {
        category: "Companies Act",
        question: "What is the difference between a profit company and an NPC?",
        answer:
            "Profit companies (Pty/Ltd, public, state-owned) trade for shareholder return. Non-Profit Companies (NPCs) are for public-benefit purposes; they may not distribute income to members. NPCs may register as PBOs with SARS for s.18A donor receipts.",
    },
);
